/**
 * Workshop Scheduler Utility
 * Automatically calculates and updates workshop timing for DAILY FREE sessions at 4:00 PM IST
 */

// Workshop configuration - Updated for DAILY 4:00 PM FREE sessions
const WORKSHOP_CONFIG = {
  // Daily sessions (no specific day restriction)
  startTime: { hour: 16, minute: 0 }, // 4:00 PM (16:00)
  endTime: { hour: 19, minute: 0 },   // 7:00 PM (19:00)
  timezone: 'Asia/Kolkata',           // IST timezone
  duration: 3, // 3 hours
  type: 'free_webinar' // Updated to reflect free webinar
};

/**
 * Get the next workshop date from current date
 * @returns {Date} Next workshop date
 */
export function getNextWorkshopDate() {
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();

  // Calculate next workshop time
  const workshopTime = WORKSHOP_CONFIG.startTime.hour * 60 + WORKSHOP_CONFIG.startTime.minute;
  const currentTime = currentHour * 60 + currentMinute;

  let nextWorkshop = new Date(now);

  if (currentTime < workshopTime) {
    // Workshop hasn't started yet today - use today
    nextWorkshop.setHours(WORKSHOP_CONFIG.startTime.hour);
    nextWorkshop.setMinutes(WORKSHOP_CONFIG.startTime.minute);
  } else {
    // Workshop has passed today - use tomorrow
    nextWorkshop.setDate(now.getDate() + 1);
    nextWorkshop.setHours(WORKSHOP_CONFIG.startTime.hour);
    nextWorkshop.setMinutes(WORKSHOP_CONFIG.startTime.minute);
  }

  nextWorkshop.setSeconds(0);
  nextWorkshop.setMilliseconds(0);

  return nextWorkshop;
}

/**
 * Get formatted workshop date string
 * @returns {string} Formatted date string
 */
export function getFormattedWorkshopDate() {
  const nextWorkshop = getNextWorkshopDate();
  const now = new Date();
  
  // Check if it's today or tomorrow
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  
  const workshopDate = new Date(nextWorkshop);
  workshopDate.setHours(0, 0, 0, 0);
  
  if (workshopDate.getTime() === today.getTime()) {
    return "Today";
  } else if (workshopDate.getTime() === tomorrow.getTime()) {
    return "Tomorrow";
  } else {
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long',
      timeZone: WORKSHOP_CONFIG.timezone
    };
    return nextWorkshop.toLocaleDateString('en-IN', options);
  }
}

/**
 * Get workshop time string
 * @returns {string} Workshop time string
 */
export function getWorkshopTimeString() {
  const startTime = `${WORKSHOP_CONFIG.startTime.hour}:${WORKSHOP_CONFIG.startTime.minute.toString().padStart(2, '0')} PM`;
  const endTime = `${WORKSHOP_CONFIG.endTime.hour}:${WORKSHOP_CONFIG.endTime.minute.toString().padStart(2, '0')} PM`;
  return `${startTime} - ${endTime} IST`;
}

/**
 * Get countdown target date (next workshop start time)
 * @returns {Date} Target date for countdown
 */
export function getCountdownTargetDate() {
  return getNextWorkshopDate();
}

/**
 * Check if it's time to update (daily at 12:00 AM)
 * @returns {boolean} True if it's update time
 */
export function isUpdateTime() {
  const now = new Date();
  const hour = now.getHours();
  const minute = now.getMinutes();
  
  return hour === 0 && minute === 0; // Daily 12:00 AM
}

/**
 * Get workshop status text
 * @returns {string} Status text
 */
export function getWorkshopStatus() {
  const now = new Date();
  const nextWorkshop = getNextWorkshopDate();
  const timeDiff = nextWorkshop.getTime() - now.getTime();
  const hoursLeft = Math.ceil(timeDiff / (1000 * 60 * 60));

  if (hoursLeft <= 1) {
    return "FREE Webinar starting soon!";
  } else if (hoursLeft <= 6) {
    return `FREE Webinar in ${hoursLeft} hours`;
  } else if (hoursLeft <= 24) {
    const formattedDate = getFormattedWorkshopDate();
    if (formattedDate === "Today") {
      return "FREE Webinar is TODAY!";
    } else if (formattedDate === "Tomorrow") {
      return "FREE Webinar is TOMORROW!";
    }
    return `FREE Webinar ${formattedDate}`;
  } else {
    return "Next FREE webinar coming soon";
  }
}

/**
 * Get workshop details object
 * @returns {Object} Workshop details
 */
export function getWorkshopDetails() {
  const nextWorkshop = getNextWorkshopDate();
  
  return {
    date: nextWorkshop,
    formattedDate: getFormattedWorkshopDate(),
    timeString: getWorkshopTimeString(),
    status: getWorkshopStatus(),
    countdown: nextWorkshop.toISOString(),
    dayOfWeek: 'Daily',
    duration: `${WORKSHOP_CONFIG.duration} hours`,
    timezone: 'IST',
    type: WORKSHOP_CONFIG.type,
    price: 'FREE'
  };
}